import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Layers, Target, BarChart3, BookOpen, PenLine, Timer, ShieldCheck, Stethoscope } from "lucide-react";
import { PublicNav, Footer } from "../components/PublicLayout";
import { DemoQuestion } from "../components/DemoQuestion";
import { PricingCards } from "../components/PricingCards";
import { SegmentedBar } from "../components/bits";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { API } from "../lib/api";

const FEATURES = [
  { icon: Stethoscope, title: "Written for PAs, not adapted", body: "Every question is written from a PA-student perspective — testing PA-level clinical reasoning, escalation and scope of practice, not recycled medical-school content." },
  { icon: PenLine, title: "True single-best-answer format", body: "Best-of-five SBAs with clinically credible distractors. Every option — right or wrong — comes with its own explanation." },
  { icon: Target, title: "Three clinically meaningful difficulty levels", body: "Easy, medium and hard questions mapped to reasoning demand, not confusing wording. Filter sessions by difficulty." },
  { icon: Timer, title: "Quiz mode and test mode", body: "Learn with instant feedback and full explanations, or simulate exam conditions and review everything at the end." },
  { icon: BarChart3, title: "Progress that shows your weak spots", body: "Per-topic progress bars break down correct, incorrect and unanswered questions so revision targets itself." },
  { icon: ShieldCheck, title: "Clinically reviewed content", body: "Questions pass through a draft → clinical review → approval workflow before publication, with UK references such as NICE and the BNF." },
];

const FAQS = [
  { q: "Who is PAMind for?", a: "Year 1 and Year 2 Physician Associate students, students preparing for university SBA examinations, candidates preparing for the Physician Associate Registration Assessment (PARA) knowledge-based assessment, and newly qualified PAs who want continued clinical revision." },
  { q: "Is PAMind affiliated with the RCP, FPA or GMC?", a: "No. PAMind is an independent educational resource and is not affiliated with or endorsed by the Faculty of Physician Associates, Royal College of Physicians, General Medical Council or any UK university. Our content is designed to support preparation for UK PA university assessments and the PARA." },
  { q: "What is the PARA?", a: "The Physician Associate Registration Assessment is the current UK registration assessment for physician associates, consisting of a 200-question online single-best-answer knowledge-based assessment and a 14-station OSCE. PAMind focuses on the written SBA component." },
  { q: "Can I cancel my subscription?", a: "Yes. The monthly plan renews automatically but can be cancelled anytime from your account page — you keep access until the end of your paid period. The 3, 6 and 12-month plans are prepaid access periods." },
  { q: "Does my progress carry over if my subscription lapses?", a: "Yes. Your complete question history, accuracy statistics and flags are kept permanently and are waiting for you when you return." },
  { q: "Are the questions real exam questions?", a: "No. All questions are original content written for PAMind, inspired by the format and learning objectives of UK PA assessments. We do not reproduce confidential or recalled examination content, and we never claim questions are guaranteed to appear in any exam." },
];

export default function Landing() {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);

  useEffect(() => {
    API.get("/topics").then(({ data }) => setTopics(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <PublicNav />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 animate-fade-up">
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-muted-foreground mb-6">
            Physician Associate Registration Assessment preparation
          </p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="hero-headline">
            The UK Physician Associate Question Bank
          </h1>
          <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl" data-testid="hero-subheading">
            PA-specific single-best-answer practice designed around UK PA education and PARA preparation — with explanations that actually teach.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Button size="lg" data-testid="hero-start-btn" onClick={() => navigate("/register")} className="px-8">
              Start Practising <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button size="lg" variant="outline" data-testid="hero-demo-btn"
              onClick={() => document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" })} className="px-8">
              View Demo Question
            </Button>
          </div>
          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-2"><Layers className="w-4 h-4 text-primary" /> 14 curriculum topics</span>
            <span className="flex items-center gap-2"><BookOpen className="w-4 h-4 text-primary" /> Explanation for every option</span>
            <span className="flex items-center gap-2"><BarChart3 className="w-4 h-4 text-primary" /> Weak-spot analytics</span>
          </div>
        </div>
        <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "150ms" }}>
          <div className="rounded-lg overflow-hidden border border-border shadow-md">
            <img src="https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Physician associate in medical uniform with stethoscope in a hospital corridor" className="w-full h-full object-cover max-h-[480px]" data-testid="hero-image" />
          </div>
        </div>
      </section>

      {/* Demo */}
      <section id="demo" className="bg-secondary/60 border-y border-border">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-20">
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-muted-foreground mb-3">Try it now — no account needed</p>
          <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mb-8" data-testid="demo-section-title">
            Experience a PAMind SBA
          </h2>
          <DemoQuestion />
          <div className="mt-10 bg-primary text-primary-foreground rounded-lg p-8 flex flex-wrap items-center justify-between gap-6" data-testid="demo-cta">
            <div>
              <h3 className="font-display font-bold text-xl">Ready to practise like a PA?</h3>
              <p className="mt-1 text-sm text-primary-foreground/70">Unlock the full question bank from £7.99/month.</p>
            </div>
            <Button size="lg" variant="secondary" data-testid="demo-cta-btn" onClick={() => navigate("/register")} className="px-8">
              Create your account <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Built for PAs */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="max-w-2xl">
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-muted-foreground mb-3">Why PAMind exists</p>
          <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight" data-testid="built-for-pas-title">
            Built specifically for PAs
          </h2>
          <p className="mt-5 text-muted-foreground leading-relaxed">
            Medical-student question banks are too broad. Existing PA resources are often too simplistic, repetitive or visually dated. PAMind occupies the gap: a serious professional platform testing clinical reasoning, investigation interpretation, management, pharmacology, professionalism and NHS-based practice — at exactly the level UK PA education demands.
          </p>
        </div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <div key={f.title} className="bg-card border border-border rounded-lg p-7 shadow-sm hover:shadow-md transition-shadow duration-200 animate-fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`feature-card-${i}`}>
              <f.icon className="w-6 h-6 text-primary" />
              <h3 className="mt-4 font-display font-semibold text-lg">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Topics */}
      <section id="topics" className="bg-secondary/60 border-y border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <p className="text-xs tracking-[0.25em] uppercase font-semibold text-muted-foreground mb-3">Curriculum coverage</p>
              <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight" data-testid="topics-section-title">14 topics. Zero filler.</h2>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Built around the UK PA curriculum — cross-specialty content like pharmacology, ethics, safeguarding and public health is woven into every subject.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {topics.map((t) => (
              <div key={t.slug} className="bg-card border border-border rounded-lg p-5 hover:shadow-md transition-shadow duration-200" data-testid={`landing-topic-${t.slug}`}>
                <h3 className="font-display font-semibold">{t.name}</h3>
                <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed line-clamp-2">{t.description}</p>
                <p className="mt-3 text-xs font-semibold text-primary">{t.published_count} published {t.published_count === 1 ? "question" : "questions"}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-muted-foreground">
            The bank is growing continuously through our clinical content pipeline — every question is reviewed before publication, and we never inflate counts with filler.
          </p>
        </div>
      </section>

      {/* Difficulty + modes */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 grid lg:grid-cols-2 gap-16">
        <div>
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-muted-foreground mb-3">Difficulty system</p>
          <h2 className="font-serif text-2xl md:text-3xl font-bold tracking-tight mb-8">Clinically meaningful difficulty</h2>
          <div className="space-y-4">
            {[
              { label: "Easy", desc: "Foundational knowledge and straightforward clinical recognition — classic presentations, first-line treatments, basic interpretation.", cls: "border-success/40 bg-success/5" },
              { label: "Medium", desc: "Application of knowledge — choosing between plausible diagnoses, selecting investigations, weighing clinical details.", cls: "border-accent bg-accent/10" },
              { label: "Hard", desc: "Deeper reasoning — competing diagnoses, complications, contraindications, multi-step decisions and nuanced professional scenarios.", cls: "border-destructive/40 bg-destructive/5" },
            ].map((d) => (
              <div key={d.label} className={`border rounded-lg p-5 ${d.cls}`} data-testid={`difficulty-explainer-${d.label.toLowerCase()}`}>
                <h3 className="font-display font-semibold">{d.label}</h3>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-muted-foreground mb-3">Two ways to practise</p>
          <h2 className="font-serif text-2xl md:text-3xl font-bold tracking-tight mb-8">Quiz mode vs test mode</h2>
          <div className="space-y-4">
            <div className="bg-card border border-border rounded-lg p-6" data-testid="quiz-mode-card">
              <h3 className="font-display font-semibold">Quiz mode — for learning</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">Submit an answer and immediately see whether you were right, why the correct answer is correct, and why every distractor is wrong.</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-6" data-testid="test-mode-card">
              <h3 className="font-display font-semibold">Test mode — for exam conditions</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">Answer without feedback, then review your score, timing, difficulty breakdown and question-by-question performance at the end.</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="font-display font-semibold mb-4">Progress you can read at a glance</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-xs font-medium mb-1.5"><span>Cardiology</span><span className="text-muted-foreground">example</span></div>
                  <SegmentedBar correct={32} incorrect={9} total={80} />
                </div>
                <div className="flex gap-5 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-success inline-block" /> Correct</span>
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-destructive inline-block" /> Incorrect</span>
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-muted border border-border inline-block" /> Unanswered</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-secondary/60 border-y border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="max-w-2xl mb-12">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-muted-foreground mb-3">Pricing</p>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight" data-testid="pricing-section-title">
              Serious revision, from £7.99 a month
            </h2>
            <p className="mt-4 text-muted-foreground">Secure payment via Stripe. Cancel the monthly plan anytime. Prepaid plans keep your access for the full period — and your progress is kept permanently either way.</p>
          </div>
          <PricingCards />
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-6 lg:px-12 py-24">
        <p className="text-xs tracking-[0.25em] uppercase font-semibold text-muted-foreground mb-3">FAQ</p>
        <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mb-10" data-testid="faq-title">Frequently asked questions</h2>
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`}>
              <AccordionTrigger data-testid={`faq-question-${i}`} className="text-left font-medium">{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Disclaimer */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-20">
        <div className="border border-border rounded-lg p-6 bg-card text-xs text-muted-foreground leading-relaxed" data-testid="disclaimer">
          <strong className="text-foreground">Disclaimer:</strong> PAMind is an independent educational resource and is not affiliated with or endorsed by the Faculty of Physician Associates, Royal College of Physicians, General Medical Council or any UK university. Content is designed to support preparation for UK PA university assessments and the PARA; it does not reproduce confidential examination material and no question is guaranteed to appear in any assessment. Clinical content is educational and is not a substitute for current national guidance or clinical judgement.
        </div>
      </section>

      <Footer />
    </div>
  );
}
